import { createFileRoute, useParams } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { TrendlineTable } from "@/types/plot";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTable,
    faChartBar,
    faSliders,
    faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
    RangeValue,
    DateValue,
    Button,
    CheckboxGroup,
    Checkbox,
    Divider,
    Select,
    SelectItem,
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import dayjs from "dayjs";
import DateRangePickerWithPresets from "@/components/inputs/DateRangePickerWithPresets";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useTranslation } from "react-i18next";
import SITable from "@/components/field/insights/SITable";
import SITrendline from "@/components/field/insights/SITrendline";
import NUptake from "@/components/field/insights/NUptake";
import NUptakeTable from "@/components/field/insights/NUptakeTable";
import { useI18n } from "@/lib/i18n/useI18n";
import NStatusChip from "@/components/common/NStatusChip";
import { CropConfiguration } from "@/types/cropConfiguration";
import { CropCycleV2 } from "@/types/cropCycle";
import { ReportedGrowthStage } from "@/types/reportedGrowthStage";
import { CropStage } from "@/types/cropConfiguration";
import { Selection } from "@heroui/react";
import { Resource } from "@/types/user";
import { Link } from "@tanstack/react-router";
import useAuth from "@/lib/auth/useAuth";
import { getTodayWithTimeTravel } from "@/lib/utils/date";

export const Route = createFileRoute(
    "/dashboard/fields/$fieldId/insights/ntime",
)({
    component: RouteComponent,
});

function RouteComponent() {
    const { t } = useTranslation("insights");
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights/ntime",
    });
    const { fieldQuery, selectedImage, selectedCropCycle } =
        useFieldDashboard() ?? {};
    const { useGetRequest, useMutateRequest } = useRequest();
    const { userIsSentinel, isAtLeastDetail } = useAuth();

    const cropConfigQuery = useGetRequest<CropConfiguration>({
        url: `crop_configurations/${(selectedCropCycle as CropCycleV2)?.effective_planting_operation?.crop_configuration?.id}/`,
        enabled: !!(selectedCropCycle as CropCycleV2)
            ?.effective_planting_operation?.crop_configuration?.id,
    });

    const growthStages = useMemo(
        () => cropConfigQuery?.data?.growth_stages ?? [],
        [cropConfigQuery?.data?.growth_stages],
    );

    const hasCrop = useMemo(() => {
        return !!growthStages?.length;
    }, [growthStages?.length]);

    const reportedGrowthStagesQuery = useGetRequest<ReportedGrowthStage[]>({
        url: `reported_growth_stages/`,
        enabled: !!fieldId,
        params: {
            field: fieldId,
            start_date: dayjs(selectedCropCycle?.start_date).isValid()
                ? dayjs(selectedCropCycle?.start_date).format("YYYY-MM-DD")
                : undefined,
            end_date: dayjs(selectedCropCycle?.end_date).isValid()
                ? dayjs(selectedCropCycle?.end_date).format("YYYY-MM-DD")
                : undefined,
        },
    });

    const fieldCropStageQuery = useGetRequest<CropStage | null>({
        url: `fields/${fieldId}/crop_stage/`,
        enabled: !!fieldId,
        params: {
            target_date: dayjs(
                selectedImage?.capture_date ||
                getTodayWithTimeTravel().format("YYYY-MM-DD"),
            ).format("YYYY-MM-DD"),
            crop_cycle_id: (selectedCropCycle as CropCycleV2)?.id,
        },
    });

    useEffect(() => {
        if (reportedGrowthStagesQuery?.data?.length) {
            const growthStageOnDate = reportedGrowthStagesQuery?.data?.find(
                (row) =>
                    dayjs(row.date).isSame(
                        dayjs(selectedImage?.capture_date),
                        "day",
                    ),
            );
            if (growthStageOnDate) {
                setSelectedGrowthStage(
                    new Set([growthStageOnDate.growth_stage]),
                );
                return;
            }
        }
        setSelectedGrowthStage(
            new Set([fieldCropStageQuery?.data?.growth_stage ?? ""]),
        );
    }, [
        reportedGrowthStagesQuery?.data,
        selectedImage?.capture_date,
        fieldCropStageQuery?.data?.growth_stage,
    ]);

    const [selectedGrowthStage, setSelectedGrowthStage] = useState<Selection>();
    const setGrowthStage = useMutateRequest({
        successMessage: t("success.growth_stage_reported_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_report_growth_stage", {
            ns: "messages",
        }),
    });

    const submitGrowthStage = useCallback(
        (stage: Selection) => {
            if (!stage || stage === "all") return;
            setGrowthStage.mutate(
                {
                    url: `reported_growth_stages/`,
                    method: "POST",
                    data: {
                        id: null,
                        field: fieldId,
                        growth_stage: [...stage][0],
                        date: dayjs(selectedImage?.capture_date).format(
                            "YYYY-MM-DD",
                        ),
                    },
                },
                {
                    onSuccess: () => {
                        reportedGrowthStagesQuery.refetch();
                        fieldCropStageQuery.refetch();
                    },
                },
            );
        },
        [
            setGrowthStage,
            fieldId,
            selectedImage?.capture_date,
            reportedGrowthStagesQuery,
            fieldCropStageQuery,
        ],
    );

    const popOverContent = useMemo(() => {
        if (!fieldCropStageQuery?.data) return null;
        return (
            <div className="grid grid-cols-2 gap-2">
                <div>Growth Stage:</div>
                <div>{fieldCropStageQuery?.data?.growth_stage}</div>
                <div>DAP: </div>
                <div>{fieldCropStageQuery?.data?.days_after_planting}</div>
                <div>GDD: </div>
                <div>{fieldCropStageQuery?.data?.growing_degree_days}</div>
                <div>Nitrogen Flux:</div>
                <div>{fieldCropStageQuery?.data?.nitrogen_flux}</div>
                <div>Vegetation Index: </div>
                <div>{fieldCropStageQuery?.data?.vegetation_index}</div>
                <div>KCB:</div>
                <div>{fieldCropStageQuery?.data?.kcb}</div>
            </div>
        );
    }, [fieldCropStageQuery?.data]);

    const tableQuery = useGetRequest<TrendlineTable[]>({
        url: `fields/${fieldId}/trendline_table/`,
        enabled: !!fieldId,
        params: {
            values: "si",
            groupby: "block",
        },
    });
    const { formatDateMMDDYYYY } = useI18n();
    const [showTable, setShowTable] = useState(false);

    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>({
        start: parseDate(dayjs().subtract(2, "weeks").format("YYYY-MM-DD")),
        end: parseDate(dayjs().format("YYYY-MM-DD")),
    });

    const pairIdOptions = useMemo(() => {
        return sortArrayByKey(
            [...new Set(tableQuery?.data?.map((row) => row.pair_id) ?? [])].map(
                (pair) => ({ id: pair, description: pair }),
            ),
            "id",
        );
    }, [tableQuery?.data]);
    const [selectedPairIds, setSelectedPairIds] = useState<string[]>([
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12",
        "13",
        "14",
        "15",
        "16",
        "17",
        "18",
        "19",
        "20",
    ]);

    return (
        <div className="flex flex-col gap-2 m-1">
            <div className="flex flex-row justify-between items-start">
                <div className="flex flex-col gap-1 flex-1">
                    <div>
                        {t("imageDate", { ns: "insights" })}{" "}
                        <span>
                            {formatDateMMDDYYYY(selectedImage?.capture_date)}
                        </span>
                    </div>
                    <div className="flex flex-row gap-2">
                        {t("nStatus", { ns: "insights" })}{" "}
                        {selectedImage?.n_status && selectedImage?.usable && (
                            <>
                                <NStatusChip
                                    nStatus={selectedImage?.n_status}
                                    sufficiency={
                                        selectedImage?.si_mean
                                            ? selectedImage?.si_mean * 100
                                            : null
                                    }
                                />
                                <NStatusChip
                                    nStatus={selectedImage?.n_status}
                                />
                            </>
                        )}{" "}
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                        {t("growthStage", { ns: "insights" })}{" "}
                        <Select
                            className=" max-w-[10rem]"
                            size="sm"
                            aria-label="Growth Stage"
                            selectedKeys={selectedGrowthStage}
                            onSelectionChange={(e) => {
                                setSelectedGrowthStage(e);
                                submitGrowthStage(e);
                            }}
                            isDisabled={!hasCrop}
                            isLoading={fieldCropStageQuery?.isLoading}
                            startContent={
                                userIsSentinel ? (
                                    <Popover>
                                        <PopoverTrigger>
                                            <FontAwesomeIcon
                                                icon={faInfoCircle}
                                            />
                                        </PopoverTrigger>
                                        <PopoverContent>
                                            {popOverContent}
                                        </PopoverContent>
                                    </Popover>
                                ) : null
                            }
                        >
                            {growthStages?.map((stage) => {
                                return (
                                    <SelectItem key={stage}>{stage}</SelectItem>
                                );
                            })}
                        </Select>
                    </div>
                    <div className="text-xs italic">
                        {t("plantedOn", { ns: "insights" })}{" "}
                        {dayjs(
                            (selectedCropCycle as CropCycleV2)
                                ?.effective_planting_operation?.start_date,
                        ).format("ll")}
                        .
                    </div>
                </div>
                <div className="flex flex-row gap-2">
                    {isAtLeastDetail(Resource.INSIGHTS_CONFIGURATION) && (
                        <Button
                            isIconOnly
                            variant="flat"
                            as={Link}
                            to="/dashboard/fields/$fieldId/insights/settings"
                            search={{
                                // @ts-expect-error - Button -> Link
                                advisor: undefined,
                            }}
                            params={
                                {
                                    fieldId,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any
                            }
                            size="sm"
                            aria-label="Insights Settings"
                        >
                            <FontAwesomeIcon icon={faSliders} />
                        </Button>
                    )}
                </div>
            </div>
            <Divider />
            <div className="flex flex-row gap-2 w-full items-end">
                <DateRangePickerWithPresets
                    value={dateRange}
                    onChange={setDateRange}
                    ariaLabel="Date Range Picker"
                />
                <Button
                    isIconOnly
                    size="sm"
                    variant="flat"
                    onPress={() => setShowTable(!showTable)}
                    aria-label="Toggle Table"
                    className="mt-3"
                >
                    <FontAwesomeIcon icon={!showTable ? faTable : faChartBar} />
                </Button>
            </div>
            {pairIdOptions.length && (
                <div className="flex flex-col">
                    <div className="font-semibold text-sm">
                        {t("plotPairs", {
                            ns: "insights",
                        })}
                    </div>
                    <CheckboxGroup
                        orientation="horizontal"
                        value={selectedPairIds}
                        onValueChange={setSelectedPairIds}
                        size="sm"
                    >
                        {pairIdOptions.map((option) => (
                            <Checkbox
                                key={option.id}
                                value={option.id.toString()}
                            >
                                {option.description}
                            </Checkbox>
                        ))}
                    </CheckboxGroup>
                </div>
            )}
            {showTable ? (
                <>
                    <SITable
                        fieldId={fieldId}
                        trendlineQuery={tableQuery}
                        dateRange={dateRange}
                        pairIds={selectedPairIds}
                    />
                    <Divider />
                    <NUptakeTable dateRange={dateRange} />
                </>
            ) : (
                <>
                    <SITrendline
                        fieldId={fieldId}
                        senstiivity={fieldQuery?.data?.insight_sensitivity}
                        tableQuery={tableQuery}
                        dateRange={dateRange}
                        selectedPairIds={selectedPairIds}
                    />
                    <Divider />
                    <NUptake dateRange={dateRange} />
                </>
            )}
        </div>
    );
}
